import React from "react";
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import { Select } from "../../../../../common/Select"; 
import { TextArea } from "../../../../../common/Input"; 

export function AppraisalTab({ data, evaluationData, onEvaluationChange, legendDefinitions }) {
  
  const ratingOptions = legendDefinitions
    .sort((a, b) => a.rating - b.rating)
    .map(def => ({
      value: def.rating.toString(),
      label: `(${def.rating}) - ${def.code}`
    }));

  const getBorderColor = (categoryId) => {
    const colors = {
      1: 'border-l-blue-500',
      2: 'border-l-green-500',
      3: 'border-l-purple-500',
      4: 'border-l-amber-500',
      5: 'border-l-red-500'
    };
    return colors[categoryId] || 'border-l-gray-400';
  };

  return (
    <div className="space-y-6">
      {/* RESTORED: Header Info (Employee/Rater cards) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Employee Card */}
        <div className="flex items-center space-x-4 p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="p-3 bg-blue-100 rounded-lg">
            <PersonIcon className="text-blue-600" fontSize="medium" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Ratee / Employee</p>
            <h4 className="text-base font-bold text-gray-800">{data.employeeName}</h4>
            <p className="text-xs text-gray-500 font-medium">
              {data.position} <span className="text-gray-300 mx-1">|</span> {data.department}
            </p>
          </div>
        </div>

        {/* Rater Card */}
        <div className="flex items-center space-x-4 p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="p-3 bg-orange-100 rounded-lg">
            <AssignmentIndIcon className="text-orange-600" fontSize="medium" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Appraiser / Rater</p>
            <h4 className="text-base font-bold text-gray-800">{data.appraiserName}</h4>
            <p className="text-xs text-gray-500 font-medium">
              Period: <span className="text-blue-600 font-bold">{data.period}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Evaluation Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md">
        <table className="min-w-full border-collapse">
          <thead className="bg-blue-500 text-white">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-widest">Objectives & KPI Criteria</th>
              <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-widest w-32">Weight</th>
              <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-widest w-80">Rating & Comments</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.performanceCategories.map((cat) => (
              <React.Fragment key={cat.id}>
                {/* Category Header Row */}
                <tr className="bg-gray-50/100">
                  <td colSpan="3" className="px-6 py-3 text-sm font-bold text-gray-600 border-l-4 border-gray-300">
                    <div className="flex items-center space-x-2">
                        <span className="flex items-center justify-center p-1 bg-white rounded shadow-sm">
                            <TrackChangesIcon className="text-blue-500" fontSize="small" />
                        </span>
                        <span className="tracking-tight uppercase">CATEGORY: {cat.description}</span>
                    </div>
                  </td>
                </tr>
                
                {cat.objectives.map((obj) => (
                  <tr 
                    key={obj.id} 
                    className={`hover:bg-blue-50/10 transition-colors border-l-4 ${getBorderColor(cat.id)}`}
                  >
                    <td className="px-10 py-5 align-top">
                      <div className="font-bold text-gray-600 text-sm tracking-tight">{obj.code}</div>
                      <div className="text-xs text-gray-500 mt-1 italic leading-relaxed">{obj.description}</div>
                    </td>
                    <td className="px-6 py-5 text-center align-top">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                        {obj.percentage}%
                      </span>
                    </td>
                    <td className="px-6 py-5 space-y-3">
                      <Select 
                        value={evaluationData[obj.id]?.score || ""}
                        onChange={(e) => onEvaluationChange(obj.id, 'score', e.target.value)}
                        options={ratingOptions}
                        placeholder="Select Rating"
                        parentClassName="mb-0" 
                        className="py-2 font-medium"
                      />
                      <TextArea 
                        placeholder="Enter rater's comments/justification..."
                        value={evaluationData[obj.id]?.comment || ""}
                        onChange={(e) => onEvaluationChange(obj.id, 'comment', e.target.value)}
                        rows={2}
                        className="text-xs"
                      />
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}